const DROPDOWN_NAMES = ["forecasts", "favourites", "account", "settings"] as const
const INITIALLY_OPEN = "forecasts"

type DropdownName = typeof DROPDOWN_NAMES[number]

type Dropdown = {
  name: DropdownName
  button?: HTMLElement
  panel?: HTMLElement
}

export class MobileDropdowns {
  private cssClass: string
  private dropdowns: Dropdown[] = []
  private abortController = new AbortController()

  constructor(cssClass: string) {
    this.cssClass = cssClass
    this.initializeElements()
    this.initializeDropdownStates()
    this.addEventListeners()
  }

  private initializeElements() {
    this.dropdowns = DROPDOWN_NAMES.map((name) => ({
      name,
      button:
        document.querySelector<HTMLElement>(`.mobile-menu .${this.cssClass}__button--${name}`) ??
        undefined,
      panel:
        document.querySelector<HTMLElement>(`.mobile-menu .${this.cssClass}__dropdown--${name}`) ??
        undefined,
    }))
  }

  private initializeDropdownStates() {
    this.dropdowns.forEach(({ name, button, panel }) => {
      if (name === INITIALLY_OPEN) {
        button?.setAttribute("aria-expanded", "true")
        panel?.classList.add("active")
        panel?.setAttribute("aria-hidden", "false")
      } else {
        button?.setAttribute("aria-expanded", "false")
      }
    })
  }

  private addEventListeners() {
    const { signal } = this.abortController

    this.dropdowns.forEach((dropdown) => {
      dropdown.button?.addEventListener(
        "click",
        (event) => {
          event.preventDefault()
          this.toggleDropdown(dropdown)
        },
        { signal },
      )
    })
  }

  private arrowIcon(button: HTMLElement): Element | null {
    return button.querySelector(`.${this.cssClass}__down-arrow-icon`)
  }

  private toggleDropdown(dropdown: Dropdown): void {
    const { button, panel } = dropdown
    if (!button || !panel) return

    const isExpanded = button.getAttribute("aria-expanded") === "true"
    this.closeAllDropdowns(panel)

    if (isExpanded) {
      panel.classList.remove("active")
      button.setAttribute("aria-expanded", "false")
      panel.setAttribute("aria-hidden", "true")
      this.arrowIcon(button)?.classList.remove("active")
    } else {
      panel.classList.add("active")
      button.setAttribute("aria-expanded", "true")
      panel.setAttribute("aria-hidden", "false")
      this.arrowIcon(button)?.classList.add("active")
    }
  }

  // check if click is inside dropdowns
  isDropdownClick(target: Node): boolean {
    return this.dropdowns.some(({ panel }) => panel?.contains(target))
  }

  // check if click is on dropdown buttons
  isDropdownButtonClick(target: Node): boolean {
    return this.dropdowns.some(({ button }) => button?.contains(target))
  }

  closeAllDropdowns(exceptPanel?: HTMLElement): void {
    this.dropdowns.forEach(({ button, panel }) => {
      if (!button || !panel || panel === exceptPanel) return

      if (panel.classList.contains("active")) {
        panel.classList.remove("active")
        button.setAttribute("aria-expanded", "false")
      }

      this.arrowIcon(button)?.classList.remove("active")
    })
  }

  destroy(): void {
    this.abortController.abort()
  }
}
